import type { ReactNode } from "react";
import { Badge } from "../badge";
import type { BausteinSymbol } from "../symbol-typ";

/** Eine Kennzahl des Bands. Zahlen kommen fertig vom Server; der Baustein formatiert nur (de-DE) und prüft keine Rechte. */
export interface BandKennzahl {
  label: string;
  /** Zahl (deutsch formatiert) oder fertiger Text; `null` = Platzhalter „—“ (nicht verfügbar). */
  wert: number | string | null;
  /** Graue Zeile unter dem Wert (Geltungsbereich, Herkunft). */
  zusatz: string;
  /** Lucide-Symbol links (bzw. oberhalb auf schmalen Breiten). */
  symbol: BausteinSymbol;
  /** Pille neben dem Zusatz (Warnung), nur wo der Aufrufer einen Hinweis hat. */
  pille?: string;
  /** Wert in Primärfarbe; Kennzahlen mit Warnhinweis bleiben neutral. */
  hervorgehoben?: boolean;
}

const ZAHL = new Intl.NumberFormat("de-DE");

/** Spaltenzahl ab `lg`: feste Klassenketten je Anzahl (nie zusammengesetzt). */
const SPALTEN: Record<2 | 3 | 4, string> = {
  2: "grid grid-cols-2 lg:grid-cols-2 bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden",
  3: "grid grid-cols-2 lg:grid-cols-3 bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden",
  4: "grid grid-cols-2 lg:grid-cols-4 bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden",
};

/**
 * Kennzahlenband (Katalog `grouped-kpi-cards`; M-006 HRWorks, M-153 Analytics-Auswertung): zwei bis vier Werte in EINER Karte mit
 * Trennlinien. Mobil 2 × 2, ab `lg` eine Zeile. Nicht für Kennzahlen mit Absprung (dafür `KpiKarte`) und nicht für die Kandidaten des
 * Mustervergleichs (`KennzahlKachelA` bis `D`); jedes Feld ist reine Darstellung, Beschäftigtenbezug nur als Gesamtzahl (r177/r179).
 */
export function KennzahlenBand({
  id,
  kennzahlen,
  beschriftung = "Kennzahlen",
}: {
  id?: string;
  /** Zwei bis vier Einträge; mehr werden nicht gezeigt. */
  kennzahlen: readonly BandKennzahl[];
  /** Zugänglicher Name des Bands. */
  beschriftung?: string;
}): ReactNode {
  const liste = kennzahlen.slice(0, 4);
  const anzahl = Math.min(4, Math.max(2, liste.length)) as 2 | 3 | 4;
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-kpi-cards/grouped-kpi-cards */}
      <section id={id} aria-label={beschriftung} className={SPALTEN[anzahl]}>
        {liste.map((k) => {
          const Symbol = k.symbol;
          return (
            <div
              key={k.label}
              className="block p-4 md:p-5 relative bg-layer before:absolute before:top-0 before:inset-s-0 before:w-full before:h-px lg:before:h-full before:border-s before:border-layer-line first:before:bg-transparent"
            >
              <div className="flex flex-col lg:flex-row gap-y-3 gap-x-5">
                <Symbol className="shrink-0 size-5 text-muted-foreground-1" aria-hidden="true" />
                <div className="grow">
                  <p className="text-xs uppercase font-medium text-foreground">{k.label}</p>
                  <h3 className={k.hervorgehoben ? "mt-1 text-xl sm:text-2xl font-semibold text-primary" : "mt-1 text-xl sm:text-2xl font-semibold text-foreground"}>
                    {k.wert === null ? (
                      <span role="img" aria-label="nicht verfügbar">
                        —
                      </span>
                    ) : typeof k.wert === "number" ? (
                      ZAHL.format(k.wert)
                    ) : (
                      k.wert
                    )}
                  </h3>
                  <div className="mt-1 flex flex-wrap justify-between items-center gap-2">
                    <p className="text-sm text-muted-foreground-1">{k.zusatz}</p>
                    {k.pille && <Badge ton="warnung">{k.pille}</Badge>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
}
